var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useContext, useMemo } from 'react';
import debounce from 'lodash-es/debounce';
import Fuse from 'fuse.js';
import useStyles from './AddressLookupCollection.styles';
import townsFromUK from '../../utils/ukTowns';
import townsFromIE from '../../utils/ieTowns';
import { SiteContext } from '../../utils/common';
import TextField from '../TextField/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import clsx from 'clsx';
import { COLLECTION_ADDRESS_LABEL, COLLECTION_ADDRESS_PLACEHOLDER, COLLECTION_ADDRESS_REQUIRED_MESSAGE, } from '../../utils/constants';
import CloseIcon from '@mui/icons-material/Close';
import { useFormikContext } from 'formik';
const AddressLookupCollection = ({ onSelect, initialValue = '', onClear }) => {
    const { classes } = useStyles();
    const formikContext = useFormikContext();
    const { countryCode } = useContext(SiteContext);
    const towns = countryCode === 'IE' ? townsFromIE : townsFromUK;
    const [query, setQuery] = useState(initialValue);
    const [results, setResults] = useState([]);
    const [show, setShow] = useState(false);
    const [hovered, setHovered] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const wrapperRef = useRef(null);
    const [debouncedQuery, setDebouncedQuery] = useState(query);
    // create a stable debounced setter
    const debouncedSetter = useMemo(() => debounce(setDebouncedQuery, 200), [setDebouncedQuery]);
    const fuse = new Fuse(towns, {
        keys: ['LOCATION_NAME'],
        threshold: 0.1,
        minMatchCharLength: 3,
    });
    useEffect(() => {
        if (debouncedQuery.length < 3) {
            setResults([]);
            setShow(false);
            return;
        }
        setResults(fuse
            .search(debouncedQuery)
            .map((result) => {
            const item = result.item;
            return Object.assign(Object.assign({}, item), { LOCATION_LONGITUDE_DEG: String(item.LOCATION_LONGITUDE_DEG), LOCATION_LATITUDE_DEG: String(item.LOCATION_LATITUDE_DEG) });
        })
            .slice(0, 100));
        setShow(true);
        setHovered(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery]);
    // update debouncedQuery when user types
    useEffect(() => {
        debouncedSetter(query);
        return () => {
            debouncedSetter.cancel();
        };
    }, [query, debouncedSetter]);
    useEffect(() => {
        const handleClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target))
                setShow(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);
    // Clear error once processing finishes
    useEffect(() => {
        if (!isProcessing) {
            formikContext.setFieldError('collectionAddress', undefined);
            formikContext.setFieldTouched('collectionAddress', false);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isProcessing]);
    useEffect(() => {
        setQuery(initialValue);
    }, [initialValue]);
    const handleSelect = (address) => __awaiter(void 0, void 0, void 0, function* () {
        setIsProcessing(true);
        setQuery(`${address.LOCATION_NAME}, ${address.COUNTY_NAME}`);
        setShow(false);
        formikContext.setFieldValue('collectionAddress', `${address.LOCATION_NAME}, ${address.COUNTY_NAME}`);
        formikContext.setFieldError('collectionAddress', undefined); // Clear error
        formikContext.setFieldTouched('collectionAddress', false); // Clear touched so no error shows
        if (onSelect)
            yield onSelect(address);
        setIsProcessing(false);
    });
    const handleInputFocus = () => {
        if (query.length >= 3 && results.length > 0) {
            setShow(true); // Open the dropdown
            formikContext.setFieldError('collectionAddress', undefined);
            formikContext.setFieldTouched('collectionAddress', false); // Clear touched to hide any previous error
        }
    };
    const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        formikContext.setFieldValue('collectionAddress', value);
        formikContext.setFieldError('collectionAddress', undefined); // clear any existing error
        formikContext.setFieldTouched('collectionAddress', false); // reset touched state
        // if user has typed enough characters, show suggestions
        if (value.trim().length >= 3) {
            setShow(true);
        }
        else {
            setShow(false);
        }
    };
    // Keyboard navigation handler
    const handleKeyDown = (e) => {
        if (!show || results.length === 0)
            return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHovered((prev) => {
                if (prev === null || prev === results.length - 1)
                    return 0;
                return prev + 1;
            });
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHovered((prev) => {
                if (prev === null || prev === 0)
                    return results.length - 1;
                return prev - 1;
            });
        }
        else if (e.key === 'Enter') {
            if (hovered !== null && results[hovered]) {
                e.preventDefault();
                handleSelect(results[hovered]);
            }
        }
    };
    // Helper to highlight matched text
    const highlightMatch = (text, match) => {
        if (!match || match.length < 2)
            return text;
        const lowerText = text.toLowerCase();
        const lowerMatch = match.toLowerCase();
        const matches = [];
        let i = 0;
        while (i <= lowerText.length - lowerMatch.length) {
            const idx = lowerText.indexOf(lowerMatch, i);
            if (idx === -1)
                break;
            matches.push([idx, idx + lowerMatch.length]);
            i = idx + lowerMatch.length;
        }
        if (matches.length === 0)
            return text;
        const parts = [];
        let lastIndex = 0;
        matches.forEach(([start, end]) => {
            const segment = text.slice(lastIndex, start);
            if (segment) {
                parts.push(_jsx("span", { children: segment }, `seg-${segment}`));
            }
            const matchStr = text.slice(start, end);
            parts.push(_jsx("b", { children: matchStr }, `match-${matchStr}`));
            lastIndex = end;
        });
        const tail = text.slice(lastIndex);
        if (tail) {
            parts.push(_jsx("span", { children: tail }, `seg-${tail}`));
        }
        return _jsx(_Fragment, { children: parts });
    };
    // Clear input handler
    const handleClear = (e) => {
        e.stopPropagation();
        setQuery('');
        setResults([]);
        setShow(false);
        formikContext.setFieldValue('collectionAddress', '');
        formikContext.setFieldError('collectionAddress', undefined); // clear error
        formikContext.setFieldTouched('collectionAddress', false);
        onClear && onClear();
    };
    // On blur, if value is not in addresses, show appropriate message
    const handleInputBlur = () => {
        const trimmed = query.trim();
        if (trimmed === '') {
            // empty input
            formikContext.setFieldError('collectionAddress', 'This field is required');
            formikContext.setFieldTouched('collectionAddress', true);
        }
        else {
            const normalized = (str) => str.replace(/\s+/g, ' ').trim().toLowerCase();
            const found = towns.some((a) => normalized(`${a.LOCATION_NAME}, ${a.COUNTY_NAME}`) === normalized(trimmed));
            if (!found) {
                // typed but not selected
                formikContext.setFieldError('collectionAddress', COLLECTION_ADDRESS_REQUIRED_MESSAGE);
                formikContext.setFieldTouched('collectionAddress', true);
            }
            else {
                formikContext.setFieldError('collectionAddress', undefined);
                formikContext.setFieldTouched('collectionAddress', false);
            }
        }
        setShow(false);
    };
    return (_jsxs("div", { ref: wrapperRef, className: classes.wrapper, children: [_jsx("div", { className: classes.inputWrapper, children: _jsx(TextField, { name: "collectionAddress", label: COLLECTION_ADDRESS_LABEL, hideRequiredOptional: true, value: query, onChange: handleInputChange, placeholder: COLLECTION_ADDRESS_PLACEHOLDER, fullWidth: true, onFocus: handleInputFocus, onBlur: handleInputBlur, autoComplete: "off", onKeyDown: handleKeyDown, readOnly: isProcessing, className: clsx(isProcessing && classes.textFieldLoading), inputProps: {
                        className: classes.textField,
                    }, endAdornment: isProcessing ? (_jsx(CircularProgress, { size: 20, className: classes.loader })) : (query && (_jsx("button", { type: "button", onClick: handleClear, className: classes.clearButton, tabIndex: 0, disabled: isProcessing, children: _jsx(CloseIcon, {}) }))) }) }), show && results.length > 0 && (_jsx("ul", { className: classes.list, children: results.map((addr, idx) => (_jsx("li", { className: classes.option, children: _jsxs("button", { type: "button", onMouseDown: () => handleSelect(addr), onMouseEnter: () => setHovered(idx), onMouseLeave: () => setHovered(null), className: classes.optionButton, children: [highlightMatch(addr.LOCATION_NAME, debouncedQuery), ", ", highlightMatch(addr.COUNTY_NAME, debouncedQuery)] }) }, `${addr.LOCATION_NAME}-${addr.COUNTY_NAME}`))) }))] }));
};
export default AddressLookupCollection;
